import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  deleteUser,
  signOut,
} from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { ArrowRight, Check, ChevronLeft } from "lucide-react";
import CryptoJS from "crypto-js";
import { toast } from "sonner";

import { auth, db } from "../firebase";
import SecretCodeInput from "../components/SecretCodeInput";
import EmailVerification from "../components/EmailVerification";
import RegisterForm from "../components/RegisterForm";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  RegisterServiceType,
  SECRET_CODE_CONFIG,
  SNACK_SUBSCRIPTION_DOMAIN_CONFIG,
  SNACK_SUBSCRIPTION_DOMAIN_OPTIONS,
  SNACK_SUBSCRIPTION_SECRET_CODE_CONFIG,
  SNACK_SUBSCRIPTION_SECRET_CODE_LENGTH,
  SnackSubscriptionEmailDomain,
} from "../constants/registration";

const initialErrors = {
  name: "",
  contact: "",
  role: "",
  password: "",
  confirmPassword: "",
};

type ServiceOption = {
  type: RegisterServiceType;
  title: string;
  description: string;
  summary: string;
  footer: string;
  chips: string[];
  imageSrc: string;
  imageClass: string;
  palette: {
    badgeClass: string;
    topClass: string;
    glowClass: string;
    footerClass: string;
  };
};

const serviceOptions: ServiceOption[] = [
  {
    type: RegisterServiceType.SnackSubscription,
    title: "스낵 구독 서비스",
    description: "우리 회사에 딱 맞춘 스낵을 정기적으로 배송/세팅해 드립니다.",
    summary: "스낵 구독 고객사 전용",
    footer: "#AI큐레이션 #스낵정기배송/셋팅",
    chips: ["비밀코드 확인", "이메일 인증", "가입 정보 입력"],
    imageSrc: "/img_subscribe_cutout.png",
    imageClass:
      "h-[138px] w-[138px] right-4 bottom-[-14px] sm:h-[152px] sm:w-[152px] sm:right-5 sm:bottom-[-20px]",
    palette: {
      badgeClass: "bg-white/85 text-[#fb5120]",
      topClass: "bg-[linear-gradient(180deg,#ffe7dd_0%,#fff2eb_100%)]",
      glowClass: "bg-[#ffd7c8]",
      footerClass: "text-[#d9481c]",
    },
  },
  {
    type: RegisterServiceType.UnmannedConvenience,
    title: "무인 편의점 서비스",
    description: "복지포인트를 사용해 무인 편의점을 자유롭게 이용해 보세요.",
    summary: "무인 편의점 고객사 전용",
    footer: "#우리회사편의점 #복지포인트사용",
    chips: ["비밀코드 확인", "이메일 인증", "가입 정보 입력"],
    imageSrc: "/img_store_cutout.png",
    imageClass:
      "h-[134px] w-[152px] right-2 bottom-[-12px] sm:h-[146px] sm:w-[170px] sm:right-3 sm:bottom-[-18px]",
    palette: {
      badgeClass: "bg-white/85 text-[#2563eb]",
      topClass: "bg-[linear-gradient(180deg,#dff2ff_0%,#eef8ff_100%)]",
      glowClass: "bg-[#dbeafe]",
      footerClass: "text-[#1d4ed8]",
    },
  },
];

const RegisterScreen: React.FC = () => {
  const navigate = useNavigate();
  const verificationIntervalRef = useRef<number | null>(null);
  const pendingVerificationUidRef = useRef<string | null>(null);

  const [serviceType, setServiceType] = useState<RegisterServiceType | null>(
    null,
  );
  const [isSecretCodeValid, setIsSecretCodeValid] = useState(false);
  const [secretCode, setSecretCode] = useState("");

  const [email, setEmail] = useState("");
  const [emailLocalPart, setEmailLocalPart] = useState("");
  const [selectedDomain, setSelectedDomain] = useState<
    SnackSubscriptionEmailDomain | ""
  >("");

  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [role, setRole] = useState("");
  const [affiliation, setAffiliation] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessBox, setShowSuccessBox] = useState(false);

  const [errors, setErrors] = useState(initialErrors);

  const isSnackSubscription =
    serviceType === RegisterServiceType.SnackSubscription;

  const selectedServiceMeta = useMemo(
    () => serviceOptions.find((option) => option.type === serviceType) ?? null,
    [serviceType],
  );

  const selectedDomainMeta = useMemo(() => {
    if (!selectedDomain) return null;
    return SNACK_SUBSCRIPTION_DOMAIN_CONFIG[selectedDomain];
  }, [selectedDomain]);

  useEffect(() => {
    if (!isSnackSubscription) return;

    setRole("user");
    setAffiliation(selectedDomainMeta?.affiliation ?? "");

    const nextEmail =
      emailLocalPart.trim() && selectedDomain
        ? `${emailLocalPart.trim()}${selectedDomain}`
        : "";
    setEmail(nextEmail);
  }, [
    emailLocalPart,
    isSnackSubscription,
    selectedDomain,
    selectedDomainMeta?.affiliation,
  ]);

  useEffect(() => {
    return () => {
      if (verificationIntervalRef.current !== null) {
        window.clearInterval(verificationIntervalRef.current);
      }
    };
  }, []);

  const clearVerificationPolling = () => {
    if (verificationIntervalRef.current !== null) {
      window.clearInterval(verificationIntervalRef.current);
      verificationIntervalRef.current = null;
    }
  };

  const removePendingVerificationUser = async () => {
    const pendingUid = pendingVerificationUidRef.current;
    if (!pendingUid) return;

    const currentUser = auth.currentUser;
    pendingVerificationUidRef.current = null;

    if (!currentUser || currentUser.uid !== pendingUid) return;

    try {
      await deleteUser(currentUser);
    } catch (deleteError) {
      console.error(deleteError);
      await signOut(auth).catch(() => undefined);
    }
  };

  const resetVerificationState = () => {
    clearVerificationPolling();
    setVerificationSent(false);
    setIsVerified(false);
    setError("");
  };

  const resetRegisterFormState = () => {
    setName("");
    setContact("");
    setPassword("");
    setConfirmPassword("");
    setErrors(initialErrors);
    setSubmitting(false);
  };

  const handleServiceSelect = async (
    nextServiceType: RegisterServiceType | null,
  ) => {
    clearVerificationPolling();
    await removePendingVerificationUser();

    setServiceType(nextServiceType);
    setIsSecretCodeValid(false);
    setSecretCode("");
    setEmail("");
    setEmailLocalPart("");
    setSelectedDomain("");
    setRole(
      nextServiceType === RegisterServiceType.SnackSubscription ? "user" : "",
    );
    setAffiliation("");
    setShowSuccessBox(false);
    resetVerificationState();
    resetRegisterFormState();
  };

  const validateForm = () => {
    const newErrors = {
      name: name ? "" : "이름을 입력해 주세요.",
      contact: /^010\d{8}$/.test(contact)
        ? ""
        : "연락처는 '010'으로 시작하는 숫자 11자리여야 합니다.",
      role: role ? "" : "사용자 구분을 확인해 주세요.",
      password:
        /^(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
          password,
        )
          ? ""
          : "비밀번호는 소문자, 숫자, 특수문자를 포함한 8자 이상이어야 합니다.",
      confirmPassword:
        password === confirmPassword ? "" : "비밀번호가 일치하지 않습니다.",
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some((err) => err !== "");
  };

  const generateEncryptedPassword = () => {
    const array = new Uint32Array(1);
    const temporalPassword = window.crypto.getRandomValues(array)[0];
    return CryptoJS.AES.encrypt(
      temporalPassword.toString(),
      `${process.env.REACT_APP_SECRET_KEY}`,
    ).toString();
  };

  const handleSendVerificationEmail = async () => {
    try {
      setError("");

      if (!serviceType) {
        setError("먼저 가입할 서비스를 선택해 주세요.");
        return;
      }

      if (isSnackSubscription) {
        if (!emailLocalPart.trim()) {
          setError("메일 앞부분을 입력해 주세요.");
          return;
        }
        if (emailLocalPart.includes("@")) {
          setError(
            "메일 앞부분만 입력해 주세요. 도메인은 비밀코드에 따라 자동 지정됩니다.",
          );
          return;
        }
        if (!selectedDomain || !selectedDomainMeta) {
          setError("비밀코드에 연결된 고객사 도메인 정보를 확인해 주세요.");
          return;
        }
      }

      if (!/\S+@\S+\.\S+/.test(email)) {
        setError("유효한 이메일 주소를 입력해 주세요.");
        return;
      }

      clearVerificationPolling();
      await removePendingVerificationUser();

      const encryptedPassword = generateEncryptedPassword();
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        encryptedPassword,
      );
      const user = userCredential.user;
      pendingVerificationUidRef.current = user.uid;

      await sendEmailVerification(user);
      setVerificationSent(true);
      toast.success(
        "인증 메일이 전송되었습니다. 이메일을 확인하여 인증해 주세요.",
      );

      verificationIntervalRef.current = window.setInterval(async () => {
        try {
          await user.reload();
          if (!user.emailVerified) return;

          clearVerificationPolling();
          await deleteUser(user);
          pendingVerificationUidRef.current = null;
          setIsVerified(true);
          setVerificationSent(false);
          toast.success(
            "이메일 인증이 완료되었습니다. 최종 회원가입을 진행해 주세요.",
          );
        } catch (verificationError) {
          clearVerificationPolling();
          console.error(verificationError);
        }
      }, 3000);
    } catch (sendError: any) {
      setError("인증 메일 전송에 실패했습니다: " + sendError.message);
    }
  };

  const handleFinalRegister = async (): Promise<boolean> => {
    if (!serviceType) {
      setError("가입할 서비스를 먼저 선택해 주세요.");
      return false;
    }

    if (!affiliation || !role) {
      setError("소속 또는 권한 정보가 올바르지 않습니다.");
      return false;
    }

    if (!isVerified) {
      setError("이메일 인증을 완료해 주세요.");
      return false;
    }

    if (!validateForm()) {
      setError("모든 필드를 올바르게 입력해 주세요.");
      return false;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;

      const userInfo = {
        email: user.email,
        name,
        contact,
        role,
        affiliation,
        createdAt: new Date(),
        isDelete: false,
        ...(isSnackSubscription ? { isDescribe: true } : {}),
      };

      await setDoc(doc(db, "users", user.uid), userInfo);
      await setDoc(
        doc(db, `affiliations/${affiliation}/users`, user.uid),
        userInfo,
      );

      await signOut(auth);
      return true;
    } catch (registerError: any) {
      setError("회원가입에 실패했습니다: " + registerError.message);
      return false;
    }
  };

  const handleRegister = async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const ok = await handleFinalRegister();
      if (!ok) return;

      setShowSuccessBox(true);

      setTimeout(async () => {
        setShowSuccessBox(false);
        await signOut(auth);
        navigate("/login", { state: { fromRegister: true } });
      }, 3000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditEmail = async () => {
    clearVerificationPolling();
    await removePendingVerificationUser();
    setVerificationSent(false);
    setIsVerified(false);
    setError("");
  };

  const showSecretCodeStep = Boolean(serviceType) && !isSecretCodeValid;
  const showVerificationStep = Boolean(serviceType) && isSecretCodeValid;

  const currentStepIndex = !serviceType ? 0 : !isVerified ? 1 : 2;
  const currentFlowStepIndex = !serviceType
    ? 0
    : !isSecretCodeValid
      ? 0
      : !isVerified
        ? 1
        : 2;

  const stepItems = [
    {
      title: "서비스 선택",
      description: "가입 방식 결정",
    },
    {
      title: "접속 인증",
      description: serviceType ? "비밀코드 및 메일 확인" : "인증 정보 확인",
    },
    {
      title: "가입 정보",
      description: "기본 정보 입력",
    },
  ] as const;

  const currentStepMeta = !serviceType
    ? {
        title: "서비스를 선택해 주세요.",
        description:
          "가입할 서비스를 먼저 고르면 필요한 인증 단계만 이어서 보여줍니다.",
      }
    : !isSecretCodeValid
      ? {
          title: "비밀코드를 확인해 주세요.",
          description: isSnackSubscription
            ? "고객사별 비밀코드를 입력하면 메일 도메인이 자동 지정되고 이메일 인증 단계가 열립니다."
            : "안내받은 8자리 비밀코드를 입력하면 소속과 권한이 자동으로 설정됩니다.",
        }
      : !isVerified
        ? {
            title: "이메일 인증을 완료해 주세요.",
            description: isSnackSubscription
              ? "고객사 메일 주소로 인증을 완료하면 가입 정보 입력이 열립니다."
              : "사용할 이메일 주소를 인증하면 가입을 마무리할 수 있습니다.",
          }
        : {
            title: "가입 정보를 입력해 주세요.",
            description:
              "이름, 연락처, 비밀번호를 입력하면 회원가입이 완료됩니다.",
          };

  const detailRows = selectedServiceMeta
    ? [
        {
          label: "소속",
          value:
            affiliation ||
            "비밀코드 확인 후 자동 지정",
        },
        {
          label: "권한",
          value: role
            ? role === "admin"
              ? "관리자"
              : "일반 사용자"
            : "자동 지정",
        },
        {
          label: "비밀코드",
          value: secretCode || "입력 전",
        },
        ...(isSnackSubscription
          ? [
              {
                label: "도메인",
                value:
                  selectedDomainMeta?.label || "비밀코드 확인 후 자동 지정",
              },
            ]
          : []),
        {
          label: "이메일",
          value:
            email ||
            (isSecretCodeValid
              ? isSnackSubscription
                ? "아이디 입력 후 자동 구성"
                : "인증할 이메일 입력 전"
              : "비밀코드 확인 후 입력 가능"),
        },
      ]
    : [];

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,rgba(251,81,32,0.12),transparent_24%),linear-gradient(180deg,#f8fafc_0%,#eef2f7_100%)]">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        <div className="mb-6 flex items-center justify-between gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/login")}
            className="h-11 w-11 rounded-lg border-slate-200 bg-white p-0 text-slate-700 hover:bg-slate-50 hover:text-slate-700"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-semibold tracking-[0.18em] text-slate-500">
            BUDDIE MEMBERSHIP
          </div>
        </div>

        <Card className="border-slate-200 shadow-[0_16px_40px_rgba(15,23,42,0.05)]">
          <CardHeader className="space-y-3 p-5 pb-4 sm:p-6 sm:pb-4">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-600">
              <span className="h-2 w-2 rounded-full bg-[#fb5120]" />
              회원가입
            </div>
            <div className="space-y-2">
              <CardTitle className="max-w-4xl text-[1.7rem] font-bold leading-[1.14] tracking-[-0.04em] text-slate-950 sm:text-[2.1rem] lg:text-[2.3rem]">
                지금 바로 오피스버디 회원이 되어보세요.
              </CardTitle>
              <CardDescription className="max-w-3xl text-sm leading-6 text-slate-600 sm:text-base">
                이용중인 서비스를 선택한 뒤, 각 단계 대로 진행해 주세요.
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="p-5 pt-0 sm:p-6 sm:pt-0">
            <div className="rounded-2xl border-none bg-transparent p-2">
              <div className="grid gap-2 md:grid-cols-3">
                {stepItems.map((step, index) => {
                  const isDone = index < currentStepIndex;
                  const isCurrent = index === currentStepIndex;

                  return (
                    <Card
                      key={step.title}
                      className={`border transition ${
                        isCurrent
                          ? "border-[#f7c3ae] bg-[#fff7f3] shadow-[0_6px_18px_rgba(251,81,32,0.08)]"
                          : isDone
                            ? "border-emerald-200 bg-emerald-50 shadow-none"
                            : "border-slate-200 bg-white shadow-none"
                      }`}
                    >
                      <CardContent className="flex items-center gap-3 p-3 sm:p-3.5">
                        <div
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                            isCurrent
                              ? "bg-[#fb5120] text-white"
                              : isDone
                                ? "bg-emerald-600 text-white"
                                : "border border-slate-200 bg-white text-slate-500"
                          }`}
                        >
                          {isDone ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <span>{index + 1}</span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div
                            className={`text-sm font-semibold ${
                              isCurrent ? "text-slate-950" : "text-slate-900"
                            }`}
                          >
                            {step.title}
                          </div>
                          <div
                            className={`mt-0.5 text-xs leading-5 ${
                              isCurrent
                                ? "text-[#b4532a]"
                                : isDone
                                  ? "text-emerald-700"
                                  : "text-slate-500"
                            }`}
                          >
                            {step.description}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {!serviceType && (
          <section className="mt-4 grid gap-4 lg:grid-cols-2">
            {serviceOptions.map((option) => {
              return (
                <button
                  key={option.type}
                  type="button"
                  onClick={() => {
                    void handleServiceSelect(option.type);
                  }}
                  className="text-left"
                >
                  <Card className="overflow-hidden border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.04)] transition duration-200 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(15,23,42,0.08)]">
                    <div
                      className={`relative min-h-[182px] overflow-hidden px-5 py-5 ${option.palette.topClass}`}
                    >
                      <div
                        className={`absolute -right-6 -top-6 h-32 w-32 rounded-full opacity-80 blur-3xl ${option.palette.glowClass}`}
                      />
                      <img
                        src={option.imageSrc}
                        alt={option.title}
                        className={`pointer-events-none absolute z-10 select-none object-contain drop-shadow-[0_14px_30px_rgba(15,23,42,0.12)] ${option.imageClass}`}
                      />
                      <div className="relative flex h-full flex-col justify-between">
                        <div className="flex items-start justify-between gap-4">
                          <div
                            className={`rounded-full px-3 py-1 text-xs font-semibold ${option.palette.badgeClass}`}
                          >
                            {option.summary}
                          </div>
                          <div className="h-16 w-16" />
                        </div>

                        <div className="text-sm font-medium text-slate-500">
                          {/* Buddie membership */}
                        </div>
                      </div>
                    </div>

                    <CardHeader className="space-y-2 p-5 pb-3">
                      <CardTitle className="text-[1.85rem] font-semibold tracking-[-0.04em] text-slate-950">
                        {option.title}
                      </CardTitle>
                      <CardDescription className="text-base leading-7 text-slate-600">
                        {option.description}
                      </CardDescription>
                    </CardHeader>

                    <CardFooter className="justify-between border-t border-slate-100 px-5 py-4">
                      <span className="text-sm text-slate-500">
                        {option.footer}
                      </span>
                      <span
                        className={`flex items-center gap-2 text-sm font-semibold ${option.palette.footerClass}`}
                      >
                        <span className="flex h-6 w-6 items-center justify-center rounded-full border border-current/20 bg-white">
                          <ArrowRight className="h-3.5 w-3.5" />
                        </span>
                        선택하기
                      </span>
                    </CardFooter>
                  </Card>
                </button>
              );
            })}
          </section>
        )}

        {serviceType && (
          <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
            <div className="space-y-4">
              <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-[0_16px_40px_rgba(15,23,42,0.05)]">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-500">
                      현재 단계
                    </div>
                    <h2 className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-slate-950">
                      {currentStepMeta.title}
                    </h2>
                    <p className="mt-2 max-w-2xl text-sm leading-7 text-slate-600">
                      {currentStepMeta.description}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void handleServiceSelect(null)}
                    className="h-11 rounded-lg border-slate-200 bg-white px-4 text-slate-700 hover:bg-slate-50 hover:text-slate-700"
                  >
                    처음부터 다시
                  </Button>
                </div>
              </section>

              {showSecretCodeStep && (
                <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-[0_16px_40px_rgba(15,23,42,0.05)]">
                  <div className="mb-5">
                    <div className="text-xs font-semibold tracking-[0.18em] text-slate-400">
                      STEP 2
                    </div>
                    <h3 className="mt-2 text-xl font-semibold tracking-[-0.03em] text-slate-950">
                      비밀코드를 입력해 주세요.
                    </h3>
                    <p className="mt-2 text-sm leading-7 text-slate-600">
                      {isSnackSubscription
                        ? "고객사별 비밀코드를 입력하면 고객사 도메인이 자동 지정됩니다."
                        : "오피스버디에서 안내받은 8자리 코드를 입력하면 다음 단계가 바로 열립니다."}
                    </p>
                  </div>
                  <SecretCodeInput
                    codeLength={
                      isSnackSubscription
                        ? SNACK_SUBSCRIPTION_SECRET_CODE_LENGTH
                        : 8
                    }
                    helperText={
                      isSnackSubscription
                        ? `${SNACK_SUBSCRIPTION_SECRET_CODE_LENGTH}자리 고객사 비밀코드를 입력하면 도메인이 자동 지정됩니다.`
                        : "8자리 비밀코드를 입력하면 바로 확인합니다."
                    }
                    validateCode={(code) =>
                      isSnackSubscription
                        ? Object.prototype.hasOwnProperty.call(
                            SNACK_SUBSCRIPTION_SECRET_CODE_CONFIG,
                            code,
                          )
                        : Object.prototype.hasOwnProperty.call(
                            SECRET_CODE_CONFIG,
                            code,
                          )
                    }
                    onValidCode={(code) => {
                      if (isSnackSubscription) {
                        const config = SNACK_SUBSCRIPTION_SECRET_CODE_CONFIG[code];
                        if (!config) return;

                        setSecretCode(code);
                        setIsSecretCodeValid(true);
                        setSelectedDomain(config.domain);
                        setRole(config.role);
                        setAffiliation(config.affiliation);
                        setError("");
                        return;
                      }

                      const config = SECRET_CODE_CONFIG[code];
                      if (!config) return;

                      setSecretCode(code);
                      setIsSecretCodeValid(true);
                      setRole(config.role);
                      setAffiliation(config.affiliation);
                      setError("");
                    }}
                  />
                </section>
              )}

              {showVerificationStep && !isVerified && (
                <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-[0_16px_40px_rgba(15,23,42,0.05)]">
                  <div className="mb-5">
                    <div className="text-xs font-semibold tracking-[0.18em] text-slate-400">
                      STEP 2
                    </div>
                    <h3 className="mt-2 text-xl font-semibold tracking-[-0.03em] text-slate-950">
                      이메일 인증
                    </h3>
                    <p className="mt-2 text-sm leading-7 text-slate-600">
                      {isSnackSubscription
                        ? "고객사 메일 주소를 확인한 뒤 인증 메일을 보내드립니다."
                        : "가입에 사용할 이메일 주소를 입력하고 인증을 완료해 주세요."}
                    </p>
                  </div>
                  <EmailVerification
                    email={email}
                    setEmail={setEmail}
                    verificationMode={
                      isSnackSubscription ? "domain-select" : "full"
                    }
                    emailLocalPart={emailLocalPart}
                    setEmailLocalPart={setEmailLocalPart}
                    selectedDomain={selectedDomain}
                    setSelectedDomain={(value) =>
                      setSelectedDomain(value as SnackSubscriptionEmailDomain)
                    }
                    domainOptions={SNACK_SUBSCRIPTION_DOMAIN_OPTIONS}
                    isDomainLocked={isSnackSubscription}
                    onVerificationSent={handleSendVerificationEmail}
                    isVerified={isVerified}
                    verificationSent={verificationSent}
                    handleEditEmail={handleEditEmail}
                  />
                </section>
              )}

              {showVerificationStep && isVerified && (
                <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-[0_16px_40px_rgba(15,23,42,0.05)]">
                  <div className="mb-5">
                    <div className="text-xs font-semibold tracking-[0.18em] text-slate-400">
                      STEP 3
                    </div>
                    <h3 className="mt-2 text-xl font-semibold tracking-[-0.03em] text-slate-950">
                      가입 정보 입력
                    </h3>
                    <p className="mt-2 text-sm leading-7 text-slate-600">
                      기본 정보를 입력하고 회원가입을 완료해 주세요.
                    </p>
                  </div>
                  <RegisterForm
                    name={name}
                    contact={contact}
                    role={role}
                    affiliation={affiliation}
                    setAffiliation={setAffiliation}
                    password={password}
                    confirmPassword={confirmPassword}
                    errors={errors}
                    setName={setName}
                    setContact={setContact}
                    setRole={setRole}
                    setPassword={setPassword}
                    setConfirmPassword={setConfirmPassword}
                    handleFinalRegister={handleFinalRegister}
                    onSubmit={handleRegister}
                    validateForm={validateForm}
                    submitting={submitting}
                  />
                </section>
              )}
            </div>

            <div className="space-y-4">
              <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-[0_16px_40px_rgba(15,23,42,0.05)]">
                <div className="text-sm font-semibold text-slate-500">
                  현재 정보
                </div>
                <div className="mt-4 space-y-3">
                  {detailRows.map((row) => (
                    <div
                      key={row.label}
                      className="flex items-start justify-between gap-4 border-b border-slate-100 pb-3 last:border-b-0 last:pb-0"
                    >
                      <span className="text-sm text-slate-500">
                        {row.label}
                      </span>
                      <span className="max-w-[180px] break-words text-right text-sm font-medium text-slate-900">
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-[0_16px_40px_rgba(15,23,42,0.05)]">
                <div className="text-sm font-semibold text-slate-500">
                  진행 순서
                </div>
                <div className="mt-4 space-y-3">
                  {(selectedServiceMeta?.chips ?? []).map((item, index) => {
                    const isDone = index < currentFlowStepIndex;
                    const isCurrent = index === currentFlowStepIndex;

                    return (
                      <div key={item} className="flex items-start gap-3">
                        <div
                          className={`mt-0.5 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                            isDone
                              ? "bg-emerald-100 text-emerald-700"
                              : isCurrent
                                ? "bg-slate-900 text-white"
                                : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {isDone ? (
                            <Check className="h-3.5 w-3.5" />
                          ) : (
                            index + 1
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900">
                            {item}
                          </div>
                          <div className="mt-1 text-sm text-slate-500">
                            {index === 0
                              ? "현재 서비스가 선택되었습니다."
                              : index === 1
                                ? "인증을 완료하면 마지막 입력 단계가 열립니다."
                                : "정보 입력 후 바로 회원가입이 완료됩니다."}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}
      </div>

      {showSuccessBox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-8 text-center shadow-[0_24px_60px_rgba(15,23,42,0.18)]">
            <img
              src="/buddie_signature.png"
              alt="Buddie Signature"
              className="mx-auto mb-5 w-24"
            />
            <h2 className="text-2xl font-semibold tracking-[-0.03em] text-slate-950">
              회원가입이 완료되었습니다.
            </h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              잠시 후 로그인 페이지로 이동합니다.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterScreen;
